import React, { useEffect } from 'react';
import { useField, useFormikContext } from 'formik';
import calculateField from '../utility/calculateFields';
import classNames from '../../../utility/classNames';
import DescriptionTooltipModal from './DescriptionTooltipModal';
import Tooltip from '../../Tooltip';
import { getNumberWithCommas } from '../../../utility/formatNumbers';

function CalculatedField({ field, externalData }) {
  const {
    name: fieldName,
    label,
    description,
    layout = 'default',
    unit,
    per,
    deEmphasize = false,
    emphasize = false,
    decimalPlaces = 0,
    red,
    green,
    large,
  } = field;

  const [, meta, helpers] = useField(fieldName);
  const { value } = meta;
  const { setValue } = helpers;

  const { values: formValues } = useFormikContext();

  useEffect(() => {
    const newValue = calculateField(field, { ...externalData, ...formValues });
    setValue(newValue);
  }, [formValues, externalData]);

  const valueToDisplay = (value, unit) => {
    let numberString;
    switch (unit) {
      case '$':
        numberString = `$${getNumberWithCommas(value, decimalPlaces)}`;
        numberString = numberString.replace('$-', '-$');
        break;
      case '%':
        numberString = `${getNumberWithCommas(value, decimalPlaces)}%`;
        break;
      default:
        numberString = `${getNumberWithCommas(value, decimalPlaces)} ${
          unit || ''
        }`;
    }

    if (per) {
      numberString = (
        <span>
          {numberString}
          <span className="text-base font-normal text-neutral-500 ml-[1px]">
            /{per}
          </span>
        </span>
      );
    }

    return numberString;
  };

  const getDynamicColor = (value, emphasize, red, green) => {
    if (emphasize) {
      if (red && green) {
        return value < red
          ? 'text-warning'
          : value > green
          ? 'text-green-700'
          : '';
      } else {
        return value >= 0 ? 'text-green-700' : 'text-warning';
      }
    }
    return '';
  };

  const dynamicColor = getDynamicColor(value, emphasize, red, green);

  if (layout === 'tableNoLabel') {
    return (
      <p
        className={classNames(
          'w-full tracking-wide flex items-center justify-start',
          !deEmphasize
            ? 'text-lg text-primary-600 font-semibold'
            : 'text-base font-normal'
        )}
      >
        {unit === '$'
          ? valueToDisplay(value, unit)
          : `${valueToDisplay(value, unit)} ${unit || ''}`}
      </p>
    );
  }

  return (
    <div
      className={classNames(
        layout === 'default' && 'space-y-5 sm:mt-3 ',
        (layout === 'compact' || layout === 'beforeAfter') && 'my-0 space-y-0',
        'mt-2'
      )}
    >
      <div className={classNames('flex flex-col items-center justify-center')}>
        <div className={classNames('h-full flex items-center')}>
          <span
            className={classNames(
              'font-normal text-md text-neutral-500 text-center'
            )}
          >
            {label}&nbsp;
            {description && (
              <Tooltip
                text={description}
                heading={field.tooltipHeading}
                formula={field.tooltipFormula}
                type="info"
              />
            )}
          </span>
        </div>

        <div
          className={classNames(
            layout === 'stacked' ? 'col-span-3' : 'col-span-2',
            'relative sm:max-w-xs px-3'
          )}
        >
          <p
            className={classNames(
              'flex items-center font-semibold justify-between',
              !deEmphasize
                ? 'text-lg text-primary-600 font-semibold'
                : 'text-base font-normal',
              layout === 'compact' && 'py-1',
              fieldName === 'newMarketValue' && 'border-none'
            )}
          >
            <span
              className={classNames(
                'text-4xl xl:text-6xl font-medium font-serif',
                emphasize ? dynamicColor : 'text-neutral-900'
              )}
            >
              {valueToDisplay(value, unit)}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CalculatedField;
